import React from 'react'
import { Box, Typography, Stack, useMediaQuery, useTheme } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'

const QUALITY_ICONS = {
  buena: CheckCircleOutlineIcon,
  regular: RemoveCircleOutlineIcon,
  mala: CancelOutlinedIcon,
}

const getQualityIconKey = label => {
  const lower = (label || '').toLowerCase()
  if (lower.includes('buen') || lower.includes('good') || lower === 'simétrica' || lower === 'simetrica') return 'buena'
  if (lower.includes('mal') || lower.includes('bad') || lower.includes('poor') || lower === 'irregular') return 'mala'
  return 'regular'
}

const QualityChipSelector = React.forwardRef(
  ({ label, icon: PartIcon, options = [], selected, onChange, isComplete }, ref) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const selectedOption = options.find(o => o.value === selected)

    return (
      <Box ref={ref} sx={{ mb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {PartIcon && (
            <PartIcon
              sx={{
                fontSize: 20,
                color: isComplete ? selectedOption?.color || 'success.main' : 'text.disabled',
              }}
            />
          )}
          <Typography variant="body2" fontWeight={600} color={isComplete ? 'text.primary' : 'text.secondary'}>
            {label}
          </Typography>
          {isComplete && selectedOption && (
            <Typography
              variant="caption"
              sx={{ color: selectedOption.color, fontWeight: 700, ml: 'auto !important' }}
            >
              {selectedOption.label}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={isMobile ? 0.75 : 1.5} sx={{ flexWrap: 'nowrap' }}>
          {options.map(option => {
            const isSelected = selected === option.value
            const QIcon = QUALITY_ICONS[getQualityIconKey(option.label)]
            return (
              <Box
                key={option.value}
                onClick={() => onChange(option.value)}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: isMobile ? 0.25 : 0.75,
                  minHeight: isMobile ? 52 : 48,
                  px: isMobile ? 0.5 : 1.5,
                  py: isMobile ? 0.75 : 0,
                  borderRadius: 2,
                  border: isSelected ? '2px solid' : '1.5px solid',
                  borderColor: isSelected ? option.color : 'divider',
                  bgcolor: isSelected ? `${option.color}20` : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  userSelect: 'none',
                  '&:hover': { borderColor: option.color, bgcolor: `${option.color}0A` },
                  '&:active': { transform: 'scale(0.97)' },
                }}
              >
                {QIcon && (
                  <QIcon
                    sx={{
                      fontSize: isMobile ? 20 : 22,
                      color: isSelected ? option.color : 'text.disabled',
                      transition: 'color 0.15s',
                    }}
                  />
                )}
                <Typography
                  variant={isMobile ? 'caption' : 'body2'}
                  fontWeight={isSelected ? 700 : 500}
                  sx={{
                    color: isSelected ? option.color : 'text.primary',
                    transition: 'color 0.15s',
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            )
          })}
        </Stack>
      </Box>
    )
  },
)

QualityChipSelector.displayName = 'QualityChipSelector'

export default QualityChipSelector
